import { faCheck, faChevronDown, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Transition, Combobox as HeadlessCombobox } from '@headlessui/react';
import type { JSX } from 'react';
import React, { Fragment, memo, useState } from 'react';

import { Color } from '@/constants/color';
import Badge from '@/ui/components/Badge/Badge';
import Label from '@/ui/components/Form/Label';
import { Props, SelectOption } from '@/ui/components/Form/MultiSelect/types';
import { OptionType } from '@/ui/components/Form/Select/types';

/**
 * @deprecated Do not use this component. There is no equivalent yet at the root
 * of the UI folder — a new version should be built there using the current
 * design-system patterns (tailwind-variants + Headless UI, see src/ui/Button)
 * instead of using this legacy component.
 */
const MultiSelect = ({
  buttonIcon = faChevronDown,
  disabled = false,
  error,
  label,
  missingOptionsText = 'No options',
  name,
  onChange,
  options,
  placeholder = '',
  selectedIcon,
  selectedOptions: initialSelectedOptions = []
}: Props): JSX.Element => {
  const [selected, setSelected] = useState<SelectOption[]>(initialSelectedOptions);
  const [query, setQuery] = useState('');

  const filteredOptions =
    query === ''
      ? options
      : options.filter((option) =>
          option.label
            .toLowerCase()
            .replace(/\s+/g, '')
            .includes(query.toLowerCase().replace(/\s+/g, ''))
        );

  const handleSetSelectedOptions = (options: OptionType<string>[]) => {
    setQuery('');
    setSelected(options);
    onChange(options);
  };

  const disabledClassNames = disabled ? 'opacity-50 cursor-not-allowed' : '';

  return (
    <Label htmlFor={name} text={label}>
      <HeadlessCombobox
        disabled={disabled}
        immediate
        multiple
        onChange={handleSetSelectedOptions}
        value={selected}>
        <>
          {selected && selected.length > 0 && (
            <ul className="my-1">
              {selected.map((option) => (
                <li
                  className="inline-block mr-1"
                  key={option.value}
                  data-testid={`${name}-selected-option-${option.value}`}>
                  <Badge
                    color={Color.GRAY}
                    text={
                      <>
                        {selectedIcon && <FontAwesomeIcon className="mr-1" icon={selectedIcon} />}
                        {option.label}
                      </>
                    }
                  />
                </li>
              ))}
            </ul>
          )}

          <div className="relative">
            <HeadlessCombobox.Button
              aria-label={`Open ${label}`}
              className={`relative w-full cursor-default rounded-md bg-white text-left text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:outline-hidden focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6 ${disabledClassNames}`}>
              <HeadlessCombobox.Input
                className={`${
                  error ? 'bg-red-50 text-red-800 placeholder-red-500' : 'bg-white text-gray-900'
                } w-full rounded-md border-0 py-1.5 pl-3 pr-12 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 ${disabledClassNames}`}
                placeholder={placeholder}
                onChange={(event) => setQuery(event.target.value)}
              />

              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <FontAwesomeIcon
                  className={`${error ? ' text-red-500' : ' text-gray-400'} w-3 h-3`}
                  icon={buttonIcon}
                />
              </span>
            </HeadlessCombobox.Button>

            <Transition
              afterLeave={() => setQuery('')}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0">
              <HeadlessCombobox.Options className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-hidden sm:text-sm">
                {filteredOptions.length === 0 ? (
                  <div className="text-gray-400 px-3 py-2">{missingOptionsText}</div>
                ) : (
                  filteredOptions.map((option) => (
                    <HeadlessCombobox.Option
                      key={option.value}
                      className={({ active }) =>
                        `${
                          active ? 'bg-indigo-600 text-white' : ''
                        } relative cursor-default select-none py-2 pl-8 pr-4`
                      }
                      value={option}>
                      {({ active, selected: isSelected }) => (
                        <>
                          <span className={`${isSelected ? 'font-semibold' : ''} block truncated`}>
                            {option.label}
                          </span>

                          {isSelected && (
                            <span
                              className={`${
                                active ? 'text-white' : 'text-indigo-600'
                              } absolute inset-y-0 left-0 flex items-center pl-1.5`}>
                              <FontAwesomeIcon
                                aria-hidden="true"
                                className="h-3.5 w-3.5"
                                icon={faCheck}
                              />
                            </span>
                          )}
                        </>
                      )}
                    </HeadlessCombobox.Option>
                  ))
                )}
              </HeadlessCombobox.Options>
            </Transition>
          </div>

          {error && (
            <div className="text-red-500">
              <FontAwesomeIcon icon={faTimes} className="h-3.5 w-3.5 mr-2" />
              {error}
            </div>
          )}
        </>
      </HeadlessCombobox>
    </Label>
  );
};

export default memo(MultiSelect);
